import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faList, faCheck, faHourglassHalf, faLock } from '@fortawesome/free-solid-svg-icons';
import billingService from '../../services/billingService';
import practiceService from '../../services/practiceService';
import { toast } from 'react-toastify';
import Heading from '../../components/Common/Heading/Heading';
import ListScreenSearchBar from '../../components/Common/ListScreenSearchBar/ListScreenSearchBar';
import ListScreenPaginationBar from '../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import Table from '../../components/Common/Table/Table';
import StatusBadge from '../../components/Common/StatusBadge/StatusBadge';
import ActionButtons from '../../components/Common/ActionButtons/ActionButtons';
import EventLogSection from '../../components/EventLogSection/EventLogSection';
import '../../components/Common/ListScreenPageLayout/listScreenPage.css';
import '../PracticeManagement/PracticesList/PracticesList.css';
import { readBillingPracticeCache, writeBillingPracticeCache } from '../../utils/billingPracticeCache';

const BillingDashboard = () => {
  const navigate = useNavigate();
  const { clearBreadcrumbs } = useBreadcrumb();
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showLogSection, setShowLogSection] = useState(false);

  // Pagination & Sorting State
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState('30');
  const [sortConfig, setSortConfig] = useState({ key: 'status', direction: 'asc' }); // Default sort by status

  useLayoutEffect(() => {
    clearBreadcrumbs();
  }, [clearBreadcrumbs]);

  useEffect(() => {
    fetchPractices();
  }, []);

  const fetchPractices = async () => {
    try {
      setLoading(true);
      // Use billing dashboard endpoint for better data with patient counts
      try {
        const response = await billingService.getBillingDashboard();
        if (response.success) {
          setPractices(response.data || []);
          return;
        }
      } catch (billingError) {
        console.warn('Billing dashboard endpoint failed, using fallback:', billingError);
      }

      // Fallback to practice service
      const response = await practiceService.getPractices({ limit: 1000 });
      let practicesList = [];
      if (response && response.data) {
        if (response.data.success && response.data.data) {
          if (response.data.data.practices && Array.isArray(response.data.data.practices)) {
            practicesList = response.data.data.practices;
          } else if (Array.isArray(response.data.data)) {
            practicesList = response.data.data;
          }
        } else if (Array.isArray(response.data)) {
          practicesList = response.data;
        }
      }

      // Filter practices that have billing dates or are active
      const practicesWithBilling = practicesList.filter(p =>
        p && (p.billing_from_date || p.billing_to_date || p.status === 'A' || p.status === 2 || p.status_numeric === 2)
      );

      setPractices(practicesWithBilling);
    } catch (error) {
      console.error('Error fetching practices:', error);
      toast.error('Failed to load practices');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const formatPhone = (phone) => {
    if (!phone || phone === 'N/A') return 'N/A';
    return phone;
  };

  const handleViewReport = (practiceRow) => {
    const row = typeof practiceRow === 'object' ? practiceRow : practices.find((p) => String(p.id) === String(practiceRow));
    if (row) writeBillingPracticeCache(row);
    const id = row?.id ?? practiceRow;
    navigate(`/billing/manage/${id}`, row ? { state: { practice: row } } : undefined);
  };

  const handleViewCycleReport = (practiceId) => {
    navigate(`/billing/cycle/${practiceId}`);
  };

  // --- Filtering ---
  const filteredPractices = practices.filter(practice => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      practice.practice_name?.toLowerCase().includes(searchLower) ||
      practice.email_1?.toLowerCase().includes(searchLower) ||
      practice.phone_1?.toLowerCase().includes(searchLower) ||
      practice.id?.toString().includes(searchLower)
    );
  });

  // --- Sorting Helper ---
  const getStatusOrder = (status) => {
    if (!status) return 99;
    const s = status.toString().toUpperCase();
    if (s === 'A' || s === '2' || s === 'ACTIVE') return 1;
    if (s === 'P' || s === '3' || s === 'PENDING') return 2;
    if (s === 'L' || s === '4' || s === 'LOCKED') return 3;
    return 4;
  };

  // --- Sorting ---
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedPractices = [...filteredPractices].sort((a, b) => {
    if (!sortConfig.key) return 0;

    // Custom sort for Status
    if (sortConfig.key === 'status') {
      const statusA = a.status || a.status_numeric || '';
      const statusB = b.status || b.status_numeric || '';
      const orderA = getStatusOrder(statusA);
      const orderB = getStatusOrder(statusB);

      if (orderA < orderB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (orderA > orderB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    }

    // Default Sort
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Handle string case
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();

    // Handle null/undefined
    if (aValue == null && bValue != null) return 1;
    if (aValue != null && bValue == null) return -1;
    if (aValue == null && bValue == null) return 0;

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const pageSize = useMemo(() => {
    if (entriesPerPage === 'all') return Math.max(sortedPractices.length, 1);
    const n = parseInt(entriesPerPage, 10);
    return Number.isFinite(n) && n > 0 ? n : 30;
  }, [entriesPerPage, sortedPractices.length]);

  // --- Pagination ---
  const totalPages = Math.max(1, Math.ceil(sortedPractices.length / pageSize) || 1);
  const startIndex = (currentPage - 1) * pageSize;
  const currentPractices =
    entriesPerPage === 'all' ? sortedPractices : sortedPractices.slice(startIndex, startIndex + pageSize);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Reset page on search or page size change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, entriesPerPage]);


  const entriesSelectValue = entriesPerPage === 'all' ? 'all' : String(entriesPerPage);

  return (
    <div className="list-screen-page-container bd-page-root">
      <div className="list-screen-top-bar">
        <div className="list-screen-title-section">
          <Heading title="Practice List For Billing Report" level="h2" className="list-screen-title-text" />
        </div>
        <div className="list-screen-actions">
          <ListScreenSearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            inputId="billing-dashboard-search"
          />
        </div>
      </div>

      <div className="bd-table-container">
        <Table
          columns={[
            { key: 'id', title: 'ID', sortable: true, render: (row) => row.id, className: 'reusable-table-centered-col' },
            {
              key: 'status',
              title: 'Status',
              sortable: true,
              className: 'reusable-table-centered-col',
              render: (row) => <StatusBadge status={(row.status || row.status_numeric || 'N/A').toString()} />
            },
            { 
              key: 'practice_name', 
              title: 'Name', 
              sortable: true, 
              render: (row) => (
                <button
                  type="button"
                  className="practices-list-name-link"
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
                  onClick={() => handleViewReport(row.id)}
                >
                  {row.practice_name || 'N/A'}
                </button>
              )
            },
            { key: 'phone_1', title: 'Phone 1', render: (row) => formatPhone(row.phone_1) },
            { key: 'phone_2', title: 'Phone 2', render: (row) => formatPhone(row.phone_2) },
            { key: 'email_1', title: 'Email', render: (row) => row.email_1 || 'N/A' },
            {
              key: 'billingPeriod',
              title: 'Billing Period Date',
              render: (row) =>
                row.billing_from_date && row.billing_to_date
                  ? `${formatDate(row.billing_from_date)} - ${formatDate(row.billing_to_date)}`
                  : 'N/A'
            },
            {
              key: 'patientsStatus',
              title: 'Patients Status',
              render: (row) => (
                <div className="practice-patient-summary">
                  <div className="summary-left">
                    <div className="summary-total">Total: {row.total_patients || 0}</div>
                  </div>

                  <div className="summary-divider-v"></div>

                  <div className="summary-right">
                    <div className="summary-item">
                      <span className="summary-icon icon-active">
                        <FontAwesomeIcon icon={faCheck} />
                      </span>
                      <span className="summary-label">
                        Active (<span className="letter-a">A</span>):
                      </span>
                      <strong className="summary-count">{row.active_patients || 0}</strong>
                    </div>

                    <div className="summary-divider-h"></div>

                    <div className="summary-item">
                      <span className="summary-icon icon-pending">
                        <FontAwesomeIcon icon={faHourglassHalf} />
                      </span>
                      <span className="summary-label">
                        Pending (<span className="letter-p">P</span>):
                      </span>
                      <strong className="summary-count">{row.pending_patients || 0}</strong>
                    </div>

                    <div className="summary-divider-h"></div>

                    <div className="summary-item">
                      <span className="summary-icon icon-locked">
                        <FontAwesomeIcon icon={faLock} />
                      </span>
                      <span className="summary-label">
                        Locked (<span className="letter-l">L</span>):
                      </span>
                      <strong className="summary-count">{row.locked_patients || 0}</strong>
                    </div>
                  </div>
                </div>
              )
            },
            {
              key: 'view',
              title: 'View Report',
              className: 'reusable-table-centered-col',
              render: (row) => (
                <ActionButtons
                  onView={() => handleViewReport(row.id)}
                  hideEdit
                  hideDelete
                  extraButtons={() => (
                    <button
                      type="button"
                      className="res-action-btn bd-calendar-btn"
                      onClick={() => handleViewCycleReport(row.id)}
                      title="Billing Cycle Report"
                    >
                      <FontAwesomeIcon icon={faCalendarAlt} />
                    </button>
                  )}
                />
              )
            }
          ]}
          data={currentPractices}
          sortBy={sortConfig.key}
          sortOrder={sortConfig.direction}
          onSort={handleSort}
          isLoading={loading}
          loadingMode="skeleton"
          skeletonRows={Math.min(pageSize, 10)}
          emptyMessage="No practices found"
        />
      </div>

      {!loading && (
        <ListScreenPaginationBar
          entriesValue={entriesSelectValue}
          onEntriesChange={(value) => {
            setEntriesPerPage(value === 'all' ? 'all' : value);
            setCurrentPage(1);
          }}
          leftSlot={
            <button
              type="button"
              className="list-screen-option-btn"
              onClick={() => setShowLogSection(true)}
            >
              <FontAwesomeIcon icon={faList} className="list-screen-me-2" />
              Event Logs
            </button>
          }
          showPageSelector={entriesPerPage !== 'all' && sortedPractices.length > 0}
          currentPage={currentPage}
          totalPages={Math.max(totalPages, 1)}
          onPageChange={handlePageChange}
        />
      )}

      {showLogSection && (
        <div
          className="practices-list-modal-overlay"
          onClick={() => setShowLogSection(false)}
          role="presentation"
        >
          <div
            className="practices-list-modal-container"
            style={{ maxWidth: '900px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="billing-dashboard-event-logs-title"
          >
            <div className="practices-list-modal-header">
              <h5 id="billing-dashboard-event-logs-title" className="practices-list-modal-title">
                Event Logs
              </h5>
              <button
                type="button"
                className="practices-list-modal-close"
                onClick={() => setShowLogSection(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="practices-list-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <EventLogSection allModules />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default BillingDashboard;
